import { LessonDetailsContent } from "content";
import { Loader } from "components/Common/Loader";
import { Divider } from "components/Common/Divider";
import { Heading } from "components/Typography/Heading";
import { Space } from "components/Common/Space";
import { Button } from "components/Interactive/Button";
import { HomeworkContentExercises } from "components/Lesson";
import { HomeworkMoveModal } from "../HomeworkMoveModal";
import { useParams } from "react-router-dom";
import { useModal, useRole } from "hooks/common";
import { useIsClassroom } from "hooks/common/useIsClassroom/useIsClassroom";
import {
  useApplyMoveToAllStudents,
  useBatchMoveExercises,
} from "hooks/api/student-progress";
import { useCourseLessonChapters } from "hooks/api/courses";
import {
  ExerciseSelectionProvider,
  useExerciseSelection,
} from "context/ExerciseSelection";
import "./HomeworkContent.styles.scss";

type HomeworkContentProps = {
  /**
   * If `true` component will display loader with animation.
   * @default false
   */
  loading?: boolean;
  /**
   * Unique identifier of the lesson homework entity.
   */
  homeworkId?: number;
  /**
   * Student ID for group context (when studentId is not in URL params)
   * @default undefined
   */
  overrideStudentId?: string;
  /**
   * Props for StudentSwitcher component (for groups)
   * @default undefined
   */
  studentSwitcherProps?: {
    studentIds: number[];
    selectedStudentId?: number;
    onStudentSelect: (studentId: number) => void;
    loading?: boolean;
  };
};

const HomeworkContentInner: React.FC<HomeworkContentProps> = (props) => {
  const {
    loading = false,
    homeworkId,
    overrideStudentId,
    studentSwitcherProps,
  } = props;
  const { courseId, lessonId, studentId: urlStudentId } = useParams();
  const studentId = overrideStudentId || urlStudentId;
  const { isUserTeacher } = useRole();
  const { isClassroom } = useIsClassroom();
  const { selectedIds, selectedExerciseIds, clearSelection } =
    useExerciseSelection();
  const { mutate: batchMove, isLoading: isBatchMoving } = useBatchMoveExercises(
    studentId,
    courseId,
    lessonId
  );
  const { data: lessonChapters = [] } = useCourseLessonChapters(
    isClassroom && studentSwitcherProps ? courseId : undefined,
    isClassroom && studentSwitcherProps ? lessonId : undefined
  );
  const { applyToAll, isLoading: isApplyingToAll } = useApplyMoveToAllStudents(
    courseId,
    lessonId,
    lessonChapters
  );

  const {
    isModalOpen: isMoveModalOpen,
    openModal: openMoveModal,
    closeModal: closeMoveModal,
  } = useModal(false);

  const isMoving = isBatchMoving || isApplyingToAll;

  const removeFromHomeworkClickHandler = () => {
    if (studentSwitcherProps) {
      openMoveModal();
    } else {
      batchMove(
        { historyIds: selectedIds, homework: false },
        { onSuccess: clearSelection }
      );
    }
  };

  const handleMoveSingleStudent = () => {
    batchMove(
      { historyIds: selectedIds, homework: false },
      {
        onSuccess: () => {
          clearSelection();
          closeMoveModal();
        },
      }
    );
  };

  const handleMoveAllStudents = async () => {
    if (!studentSwitcherProps) return;
    try {
      await applyToAll(
        selectedExerciseIds,
        studentSwitcherProps.studentIds,
        false
      );
      clearSelection();
      closeMoveModal();
    } catch {
      // error already logged in hook
    }
  };

  return (
    <div className="nb-lesson-details-homework-content-container">
      {loading ? (
        <Loader />
      ) : (
        <>
          <Space
            direction="horizontal"
            justify="between"
            align="center"
            fullWidth
          >
            <Heading>
              {LessonDetailsContent.Content.Homework.TITLE}:{" "}
              {LessonDetailsContent.Content.Homework.EXERCISES}
            </Heading>
            {isUserTeacher && isClassroom && selectedIds.length > 0 && (
              <Button
                variant="secondary"
                onClick={removeFromHomeworkClickHandler}
                disabled={isMoving}
              >
                Видалити з домашньої роботи
              </Button>
            )}
          </Space>
          <Divider variant="dashed" margin="yAxis" marginSize="large" />
          <HomeworkContentExercises
            homeworkId={homeworkId}
            overrideStudentId={overrideStudentId}
            studentSwitcherProps={studentSwitcherProps}
          />
        </>
      )}
      {studentSwitcherProps && (
        <HomeworkMoveModal
          open={isMoveModalOpen}
          text="Застосувати дію для:"
          onClose={closeMoveModal}
          onSingleStudent={handleMoveSingleStudent}
          onAllStudents={handleMoveAllStudents}
          loading={isMoving}
        />
      )}
    </div>
  );
};

export const HomeworkContent: React.FC<HomeworkContentProps> = (props) => (
  <ExerciseSelectionProvider>
    <HomeworkContentInner {...props} />
  </ExerciseSelectionProvider>
);
